import type { CustomerLanguage } from './language';
export const EMAIL_TEMPLATE_KEYS = {
	waitingListCapacity: 'waiting-list-capacity',
	registrationConfirmation: 'registration-confirmation',
	eventReminder: 'event-reminder',
	registrationCancellation: 'registration-cancellation',
} as const;

export type BuiltInEmailTemplateKey =
	(typeof EMAIL_TEMPLATE_KEYS)[keyof typeof EMAIL_TEMPLATE_KEYS];

export const EMAIL_TEMPLATE_DELIVERY_PROFILES = {
	waitingListCapacity: EMAIL_TEMPLATE_KEYS.waitingListCapacity,
	registrationConfirmation: EMAIL_TEMPLATE_KEYS.registrationConfirmation,
	eventReminder: EMAIL_TEMPLATE_KEYS.eventReminder,
	registrationCancellation: EMAIL_TEMPLATE_KEYS.registrationCancellation,
} as const;

export type EmailTemplateDeliveryProfile =
	(typeof EMAIL_TEMPLATE_DELIVERY_PROFILES)[keyof typeof EMAIL_TEMPLATE_DELIVERY_PROFILES];

export const EMAIL_TEMPLATE_RUNTIME_FIELDS: Readonly<
	Record<EmailTemplateDeliveryProfile, readonly string[]>
> = {
	[EMAIL_TEMPLATE_DELIVERY_PROFILES.waitingListCapacity]: [
		'firstName',
		'eventName',
		'registrationUrl',
		'waitingListUrl',
	],
	[EMAIL_TEMPLATE_DELIVERY_PROFILES.registrationCancellation]: [
		'registrationUrl',
		'firstName',
		'eventName',
		'dateTime',
	],
	[EMAIL_TEMPLATE_DELIVERY_PROFILES.registrationConfirmation]: [
		'registrationUrl',
		'firstName',
		'eventName',
		'qrCodeUrl',
		'code',
		'dateTime',
	],
	[EMAIL_TEMPLATE_DELIVERY_PROFILES.eventReminder]: [
		'registrationUrl',
		'firstName',
		'eventName',
		'qrCodeUrl',
		'code',
		'dateTime',
	],
} as const;

export interface EmailTemplateFieldDefinition {
	name: string;
	mapping: string;
	sampleValue: string;
	description?: string;
}

export interface EmailTemplateSummary {
	language?: CustomerLanguage;
	textPart?: string;
	seasonalReviewRequired?: boolean;
	seasonalDetailsReviewed?: boolean;
	key: string;
	deliveryProfile: EmailTemplateDeliveryProfile;
	displayName: string;
	description?: string;
	subjectPart: string;
	awsTemplateName: string;
	fieldMappings: EmailTemplateFieldDefinition[];
	currentRevisionId?: string;
	currentRevisionNumber?: number;
	publishedRevisionId?: string;
	publishedRevisionNumber?: number;
	publishedOn?: false | Date;
	createdOn: Date;
	updatedOn: Date;
}

export interface EmailTemplateRevision {
	language?: CustomerLanguage;
	textPart?: string;
	seasonalReviewRequired?: boolean;
	seasonalDetailsReviewed?: boolean;
	id: string;
	templateKey: string;
	deliveryProfile: EmailTemplateDeliveryProfile;
	revisionNumber: number;
	subjectPart: string;
	htmlStoragePath: string;
	htmlFileName: string;
	fieldMappings: EmailTemplateFieldDefinition[];
	notes?: string;
	createdOn: Date;
	createdByUid?: string;
	createdByEmail?: string;
	publishedOn?: false | Date;
}

export interface EmailTemplateDetail {
	template: EmailTemplateSummary;
	revisions: EmailTemplateRevision[];
	currentHtml?: string;
}

export interface GetEmailTemplateRequest {
	key: string;
}

export interface GetEmailTemplateRevisionRequest {
	key: string;
	revisionId: string;
}

export interface GetEmailTemplateRevisionResponse {
	template: EmailTemplateSummary;
	revision: EmailTemplateRevision;
	html: string;
}

export interface SaveEmailTemplateRevisionRequest {
	createOnly?: boolean;
	language?: CustomerLanguage;
	textPart?: string;
	seasonalReviewRequired?: boolean;
	seasonalDetailsReviewed?: boolean;
	key: string;
	deliveryProfile: EmailTemplateDeliveryProfile;
	displayName: string;
	description?: string;
	awsTemplateName: string;
	subjectPart: string;
	html: string;
	fieldMappings: EmailTemplateFieldDefinition[];
	notes?: string;
}

export interface SaveEmailTemplateRevisionResponse {
	template: EmailTemplateSummary;
	revision: EmailTemplateRevision;
	html: string;
}

export interface DeleteEmailTemplateRequest {
	key: string;
}

export interface PublishEmailTemplateRequest {
	key: string;
	revisionId?: string;
}

export interface PublishEmailTemplateResponse {
	template: EmailTemplateSummary;
	revision: EmailTemplateRevision;
	renderedHtml: string;
}

export interface SendTestEmailTemplateRequest {
	language?: CustomerLanguage;
	textPart?: string;
	seasonalReviewRequired?: boolean;
	seasonalDetailsReviewed?: boolean;
	recipientEmail: string;
	deliveryProfile: EmailTemplateDeliveryProfile;
	subjectPart: string;
	html: string;
	fieldMappings: EmailTemplateFieldDefinition[];
}

export interface SendTestEmailTemplateResponse {
	recipientEmail: string;
	renderedSubject: string;
	renderedHtml: string;
}

export interface EmailTemplatePackage {
	format: 'santashop-email-template';
	version: 1;
	template: SaveEmailTemplateRevisionRequest;
}
